import { H6 } from "@sellar-io/components";
import { BuyerProductGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { DiscoveryV2ProductDialog } from "../../pages/discovery/v2/components/DiscoveryV2ProductDialog";

import { BestPricePromiseDialog } from "../bestPricePromise/BestPricePromiseDialog";
import { BadgePurple } from "../badge/Badge";

export type item = {
  quantity: number;
  description: string;
  cost: string;
  sku?: string | null;
  fullPrice?: number;
  price?: number;
  ProductId?: number;
  ParentId?: number | null;
  promotionAppliedAt: Date | null;
};

type OrderSummaryItemListProps = {
  items: item[];
  printPrice?: boolean;
};

const DiscountedPrice = ({
  productPriceTier,
}: {
  productPriceTier?: { fullPrice: number; price: number } | null;
}) => {
  if (!productPriceTier) return null;

  return (
    <div className={"flex place-content-end items-center"}>
      {productPriceTier.fullPrice > productPriceTier.price ? (
        <>
          <span className={"text-sm text-zinc-500 line-through"}>
            {formatCurrency(productPriceTier.fullPrice, "GBP")}
          </span>
          {"  "}
        </>
      ) : null}
      <H6 className={"w-20 text-right"}>
        {formatCurrency(productPriceTier.price, "GBP")}
      </H6>
    </div>
  );
};

export const OrderSummaryItemList = (
  props: OrderSummaryItemListProps,
): React.ReactElement => {
  return (
    <>
      {props.items.map((item) => (
        <OrderSummaryItemListItem
          key={item.description}
          item={item}
          printPrice={props.printPrice}
        />
      ))}
    </>
  );
};

type OrderSummaryItemListItemProps = {
  item: item;
  printPrice?: boolean;
};

export const OrderSummaryItemListItem = (
  props: OrderSummaryItemListItemProps,
): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const [bestPricePromiseProduct, setBestPricePromiseProduct] =
    useState<BuyerProductGetResponseType>();
  const productPriceTier = getProductPriceTier({ item: props.item });
  const [productDialogActive, setProductDialogActive] = useState(false);
  const parentId = props.item.ParentId;
  const productId = props.item.ProductId;
  return (
    <>
      <div
        className={twMerge(
          "grid grid-cols-[40px_1fr_80px] gap-y-2 rounded-lg px-1",
          business?.Retailer && "cursor-pointer hover:bg-neutral-100",
        )}
        onClick={() => !!business?.Retailer && setProductDialogActive(true)}
      >
        <H6>{props.item.quantity}x</H6>
        <div className="col-span-2 flex justify-between">
          <div>
            <H6>{props.item.description}</H6>
            {props.item.sku ? (
              <H6 className={"hidden print:block"}>SKU: {props.item.sku}</H6>
            ) : null}
          </div>

          <div className="flex gap-4">
            {props.item.promotionAppliedAt && (
              <BadgePurple icon="PARTY_POPPER" />
            )}
            {productPriceTier ? (
              <DiscountedPrice productPriceTier={productPriceTier} />
            ) : (
              <H6 className={twMerge("ml-auto")}>
                {props.printPrice ? props.item.cost : null}
              </H6>
            )}
          </div>
        </div>
      </div>
      {!!productId && !!parentId && (
        <>
          <DiscoveryV2ProductDialog
            source="order"
            active={productDialogActive}
            onDismiss={() => setProductDialogActive(false)}
            ParentId={parentId}
            ProductItemId={productId}
            setBestPricePromiseDialogActive={(product) => {
              setBestPricePromiseDialogActive(true);
              setBestPricePromiseProduct(product);
            }}
          />
          {business?.Retailer && (
            <BestPricePromiseDialog
              productName={bestPricePromiseProduct?.name}
              supplierName={bestPricePromiseProduct?.Supplier.Business.name}
              buyerName={business.name}
              active={bestPricePromiseDialogActive}
              onDismiss={() => setBestPricePromiseDialogActive(false)}
            />
          )}
        </>
      )}
    </>
  );
};

const getProductPriceTier = ({ item }: { item: item }) => {
  const fullPrice = item.fullPrice ?? 0;
  const price = item.price ?? 0;

  if ((fullPrice && price) || item.promotionAppliedAt) {
    return { fullPrice, price };
  }

  return undefined;
};
